import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { TableData } from '../../../../store/adminTable/table.model';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { CustomPaginatorIntl } from '../../../generic/paginator/custom-paginator';
import { MatSelectModule } from '@angular/material/select';
import { environment } from '../../../../../environments/environment';
import { HttpRequestService } from '../../../../utilities/http/request.utilities';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ViewTransactionComponent } from '../view-transaction/view-transaction.component';
import { MatSpinner } from '@angular/material/progress-spinner';
import jsPDF from 'jspdf';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatButtonToggleModule, MatCardModule, MatInputModule,
  MatTableModule, MatToolbarModule, MatSidenavModule, MatDatepickerModule, MatNativeDateModule, MatIcon, MatIconModule,
  MatPaginatorModule, MatSelectModule, MatOptionModule, MatSortModule, MatSpinner,NgxMatSelectSearchModule,
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit {

  displayedColumns: string[] = [];
  private readonly baseColumns: string[] =
  ['start_timestamp',
   'user_id',
   'equipment_code',
   'make',
   'model',
   'user_input',
   'response',
   'response_feedback',
   'feedback_reason',
   'feedback_details',
   'expandRecord',
  ];
  private readonly webaiColumns: string[] =
  ['start_timestamp',
   'user_id',
   'equipment_code',
   'make',
   'model',
   'user_input',
   'original_response',
   'response',
   'response_feedback',
   'feedback_reason',
   'feedback_details',
   'expandRecord',
  ];
  dataSource = new MatTableDataSource<TableData>();
  baseData: TableData[] = [];
  fromDate: Date | null = null;
  toDate: Date | null = null;
  filteredData = new MatTableDataSource<TableData>();
  filters = {
    make: [] as string[],
    model: [] as string[],
    user_id: [] as string[],
    equipment_code: [] as string[],
    equipment_serial_range: [] as string[],
    response: '',
    user_input: '',
    feedBack: [] as string[],
    feedBackReason: [] as string[],
    feedBackDetails: '',
    applications: [] as string[] 
  };
  hoveredRow: any = null;
  isLoading: boolean = false;
  transactionView: 'webapp' | 'm2m' | 'webai' = 'webapp';

  makeFilterCtrl = new FormControl('');
  modelFilterCtrl = new FormControl('');
  serialNumberFilterCtrl = new FormControl('');
  equipmentFilterCtrl = new FormControl('');
  userIdFilterCtrl = new FormControl('');
  feedbackFilterCtrl = new FormControl('');
  feedbackReasonFilterCtrl = new FormControl('');
  applicationsFilterCtrl = new FormControl('');
  
  uniqueMakes: string[] = [];
  uniqueModels: string[] = [];
  uniqueSerialNumbers: string[] = [];
  uniqueEquipmentCodes: string[] = [];
  uniqueUserIds: string[] = [];
  uniqueFeedbacks: string[] = [];
  uniqueFeedbackReason: string[] = [];
  uniqueApplications: string[] = [];

  compareFn = (a: string, b: string) => a === b;
  initialMakes: string[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;


  constructor(private httpRequestService: HttpRequestService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.updateDisplayedColumns();
    this.getUniqueFilterValues();
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    today.setHours(23, 59, 59, 999);

    this.fromDate = yesterday;
    this.toDate = today;
    this.loadTableData();
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'start_timestamp') {
        return item.start_timestamp ? new Date(item.start_timestamp).getTime() : 0;
      }
      return (item as any)[property] || '';
    };
  }
  setTransactionView(view: 'webapp' | 'm2m' | 'webai'): void {
    this.transactionView = view;
    this.updateDisplayedColumns();
    this.loadTableData();
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns = this.transactionView === 'webai'
      ? [...this.webaiColumns]
      : [...this.baseColumns];
  }
  getUniqueFilterValues(): void {
    this.uniqueMakes = [...new Set(this.baseData.map(item => item.make))];
    this.uniqueModels = [...new Set(this.baseData.map(item => item.model))];
    this.uniqueSerialNumbers = [...new Set(this.baseData.map(item => item.equipment_serial_range))];
    this.uniqueEquipmentCodes = [...new Set(this.baseData.map(item => item.equipment_code))];
    this.uniqueUserIds = [...new Set(this.baseData.map(item => item.user_id))];
    this.uniqueFeedbacks = [...new Set(this.baseData.map(item => item['response_feedback']))];
    this.uniqueFeedbackReason = [...new Set(this.baseData.map(item => item['feedback_reason']))];
    this.uniqueApplications = [...new Set(this.baseData.map(item => item['applications']))]; // Add this line to get unique applications
  }

  applyFilters(): void {
    this.dataSource.data = this.baseData.filter(item => {
      const matchesResponse = this.filters.response
        ? item.response?.toLowerCase().includes(this.filters.response.toLowerCase())
        : true;
      const matchesUserInput = this.filters.user_input
        ? item.user_input?.toLowerCase().includes(this.filters.user_input.toLowerCase())
        : true;
      const matchesMake =
        this.filters.make.length === 0 || this.filters.make.includes(item.make);
      const matchesModel =
        this.filters.model.length === 0 || this.filters.model.includes(item.model);
      const matchesSerialNumber =
        this.filters.equipment_serial_range.length === 0 || this.filters.equipment_serial_range.includes(item.equipment_serial_range);
      const matchesEquipmentCode =
        this.filters.equipment_code.length === 0 || this.filters.equipment_code.includes(item.equipment_code);
      const matchesUserId =
        this.filters.user_id.length === 0 || this.filters.user_id.includes(item.user_id);
      const matchesFeedback =
        this.filters.feedBack.length === 0 || this.filters.feedBack.includes(item['response_feedback']);
      const matchesFeedbackReason =
        this.filters.feedBackReason.length === 0 || this.filters.feedBackReason.includes(item['feedback_reason']);
      const matchesFeedbackDetails =
        !this.filters.feedBackDetails || item['feedback_details']?.toLowerCase().includes(this.filters.feedBackDetails.toLowerCase());
      const matchesApplications =
        this.filters.applications.length === 0 || this.filters.applications.includes(item['applications']);


        let submittedTime: number | null = null;
        if (item.start_timestamp) {
          submittedTime = new Date(item.start_timestamp).getTime();
        }

        const fromTime = this.fromDate
          ? new Date(this.fromDate.setHours(0, 0, 0, 0)).getTime()
          : null;

        const toTime = this.toDate
          ? new Date(this.toDate.setHours(23, 59, 59, 999)).getTime()
          : null;

        const matchesDate =
          (!fromTime || (submittedTime !== null && submittedTime >= fromTime)) &&
          (!toTime || (submittedTime !== null && submittedTime <= toTime));

      return matchesMake && matchesModel && matchesSerialNumber && matchesEquipmentCode && matchesUserId && matchesDate && matchesResponse && matchesUserInput && matchesFeedback && matchesFeedbackReason && matchesFeedbackDetails
      && matchesApplications;
    });
      this.updateUniqueValues();
    if (this.dataSource.sort) {
      this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort);
    }
  }

  applyDateFilter(): void {
    this.isLoading = true;
    this.loadTableData().finally(() => this.isLoading = false);
  }

  private updateUniqueValues(): void {
    const filterExcept = (field: keyof typeof this.filters): TableData[] => {
      return this.baseData.filter(item => {
        const filters = this.filters;

        const matches = (key: keyof typeof filters, value: any, accessor: any) => {
          if (key === field) return true;
          if (Array.isArray(filters[key])) {
            return filters[key].length === 0 || filters[key].includes(accessor);
          }
          return !filters[key] || (accessor || '').toLowerCase().includes(typeof filters[key] === 'string' ? (filters[key] as string).toLowerCase() : '');
        };

        return (
          matches('make', filters.make, item.make) &&
          matches('model', filters.model, item.model) &&
          matches('user_id', filters.user_id, item.user_id) &&
          matches('equipment_code', filters.equipment_code, item.equipment_code) &&
          matches('response', filters.response, item.response) &&
          matches('user_input', filters.user_input, item.user_input) &&
          matches('feedBack', filters.feedBack, item['response_feedback']) &&
          matches('feedBackReason', filters.feedBackReason, item['feedback_reason']) &&
          matches('feedBackDetails', filters.feedBackDetails, item['feedback_details']) &&
          matches('equipment_serial_range', filters.equipment_serial_range, item.equipment_serial_range) &&
          matches('applications', filters.applications, item['applications'])
        );
      });
    };

    this.uniqueMakes = [...new Set(filterExcept('make').map(item => item.make))];
    this.uniqueModels = [...new Set(filterExcept('model').map(item => item.model))];
    this.uniqueUserIds = [...new Set(filterExcept('user_id').map(item => item.user_id))];
    this.uniqueEquipmentCodes = [...new Set(filterExcept('equipment_code').map(item => item.equipment_code))];
    this.uniqueFeedbacks = [...new Set(filterExcept('feedBack').map(item => item['response_feedback']))];
    this.uniqueFeedbackReason = [...new Set(filterExcept('feedBackReason').map(item => item['feedback_reason']))];
    this.uniqueApplications = [...new Set(filterExcept('applications').map(item => item['applications']))];

  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private getTransactionsUrl(): { url: string, params: URLSearchParams } {
    const urlMap: Record<string, string> = {
      webapp: environment.integrations.transactions,
      m2m: environment.integrations.transactionsM2M,
      webai: environment.integrations.transactionsWebAI,
    };
    const url = urlMap[this.transactionView];
    const params = new URLSearchParams();
    const startDate = this.fromDate ? this.formatDate(this.fromDate) : '2024-11-03';
    const endDate = this.toDate ? this.formatDate(this.toDate) : '2024-11-06';

    params.append('start_date', startDate);
    params.append('end_date', endDate);

    return { url, params };
  }

  private async fetchTransactionsData(): Promise<TableData[]> {
    const { url, params } = this.getTransactionsUrl();

    try {
      return await new Promise((resolve, reject) => {
        this.httpRequestService.request<any>('GET', `${url}?${params}`).subscribe({
          next: (response) => {
            if (response && Array.isArray(response.transactions)) {
                const transactions = response.transactions.map((transaction: any) => ({
                user_input: transaction.user_input,
                response: (transaction.llm_response || '').replace(/^(?:#\s*)?ANSWERS:?\s*/i, ''),
                start_timestamp: transaction.start_timestamp
                  ? new Date(transaction.start_timestamp).toLocaleDateString('en-US')
                  : null,
                make: transaction.make,
                model: transaction.model,
                model_start_end: transaction.model_start_end,
                user_id: transaction.userId,
                equipment_serial_range: transaction.model_start_end
                  ? transaction.model_start_end.split('#').slice(1).join('-')
                  : null,
                pvc: transaction.pvc
                    ?transaction.pvc 
                    : null,
                equipment_code: transaction.equipment_code,
                response_feedback: transaction.answerLikeButton === true
                  ? 'Correct'
                  : transaction.answerLikeButton === false
                  ? 'Incorrect'
                  : 'N/A',
                feedback_details: transaction.feedback,
                feedback_reason: transaction.answerLikeButton === false
                  ? transaction.reason === 'wrong_document'
                  ? 'Wrong Source Document'
                  : transaction.reason === 'incorrect_answer'
                  ? 'Did not provide right answer'
                  : transaction.reason === 'missing_resource'
                  ? 'Missing resource'
                  : 'Other'
                  : 'N/A',
                applications: transaction.office_id || null,
                original_transaction_id: transaction.original_transaction_id || null,

                }));
              resolve(transactions);
            } else {
              resolve([]);
            }
          },
          error: (error) => {
            console.error('Failed to fetch transactions data', error);
            reject([]);
          }
        });
      });
    } catch (error) {
      console.error('Failed to fetch transactions data', error);
      return [];
    }
  }

  private async loadTableData(): Promise<void> {
    try {
      const data: TableData[] = await this.fetchTransactionsData() || [];
      if (this.transactionView === 'webai') {
        await this.fetchOriginalResponses(data);
      }
      this.baseData = data;
      this.dataSource.data = data;
      this.initialMakes = [...new Set(this.baseData.map(item => item.make))];
      this.getUniqueFilterValues();
      this.applyFilters();
    } catch (error) {
      console.error('Error loading table data:', error);
    }
  }

  private async fetchOriginalResponses(transactions: TableData[]): Promise<void> {
    const withOriginal = transactions.filter(t => t['original_transaction_id']);
    const uniqueIds = [...new Set(withOriginal.map(t => t['original_transaction_id']))];

    const responseMap = new Map<string, string>();
    const results = await Promise.allSettled(
      uniqueIds.map(id =>
        new Promise<{ id: string, response: string }>((resolve, reject) => {
          this.httpRequestService.request<any>('GET', `${environment.integrations.transactionFeedback}/${id}`).subscribe({
            next: (res) => resolve({ id, response: (res.llm_response || '').replace(/^(?:#\s*)?ANSWERS:?\s*/i, '') }),
            error: (err) => reject(err)
          });
        })
      )
    );

    results.forEach(result => {
      if (result.status === 'fulfilled') {
        responseMap.set(result.value.id, result.value.response);
      }
    });

    transactions.forEach(t => {
      const origId = t['original_transaction_id'];
      if (origId && responseMap.has(origId)) {
        t['original_response'] = responseMap.get(origId)!;
      }
    });
  }

  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
  getFilteredMakes(): string[] {
    const filterValue = this.makeFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueMakes.filter(make => make.toLowerCase().includes(filterValue));
  }
  
  getFilteredModels(): string[] {
    const filterValue = this.modelFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueModels.filter(model => model.toLowerCase().includes(filterValue));
  }
  
  getFilteredSerialNumbers(): string[] {
    const filterValue = this.serialNumberFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueSerialNumbers
    .filter(sn => typeof sn === 'string')
    .filter(sn => sn.toLowerCase().includes(filterValue));
  }

  getFilteredEquipments(): string[] {
    const filterValue = this.equipmentFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueEquipmentCodes
    .filter(code => typeof code === 'string')
    .filter(code => code.toLowerCase().includes(filterValue));
  }
  getFilteredUserIds(): string[] {
    const filterValue = this.userIdFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueUserIds.filter(id => id.toLowerCase().includes(filterValue));
  }
  
  getFilteredFeedbacks(): string[] {
    const filterValue = this.feedbackFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueFeedbacks.filter(fb => fb.toLowerCase().includes(filterValue));
  }
  
  getFilteredFeedbackReasons(): string[] {
    const filterValue = this.feedbackReasonFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueFeedbackReason.filter(reason => reason.toLowerCase().includes(filterValue));
  }

  getFilteredApplications(): string[] {
  const filterValue = this.applicationsFilterCtrl.value?.toLowerCase() || '';
  return this.uniqueApplications
    .filter(app => typeof app === 'string')
    .filter(app => app.toLowerCase().includes(filterValue));
}

  exportToCSV(): void {
    if (this.dataSource.data.length === 0) {
      console.warn('No data available to export.');
      return;
    }
    const headers = [
      'Make',
      'Model',
      'Serial No. Range',
      'Equipment Code',
      'UserId',
      'Submitted At',
      'User Input',
      'Response',
      'Feedback',
      'Feedback Reason',
      'Feedback Details'
    ];
    const rows = this.dataSource.data.map(item => [
      item.make || '',
      item.model || '',
      item.equipment_serial_range || '',
      item.equipment_code || '',
      item.user_id || '',
      item.start_timestamp || '',
      (item.user_input || '').replace(/"/g, '""'),
      (item.response || '').replace(/"/g, '""'),
      item['response_feedback'] || '',
      item['feedback_reason'] || '',
      (item['feedback_details'] || '').replace(/"/g, '""')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(value => `"${value}"`).join(',')
      )
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'dashboard.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportToPDF(): void {
    if (!this.dataSource?.data?.length) {
      console.warn('No data available to export.');
      return;
    }

    const pdf = new jsPDF();
    pdf.setFontSize(16);
    pdf.text('Dashboard Export', 10, 20);

    pdf.setFontSize(12);
    let yPos = 30;
    const lineSpacing = 10;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const bottomMargin = 20;
    const maxHeight = pageHeight - bottomMargin;
    let firstEntry = true;

    this.dataSource.data.forEach((item, index) => {
      const userInput = `User Input: ${item.user_input || ''}`;
      const response = `Response: ${item.response || ''}`;
      const feedback = `Feedback: ${item['response_feedback'] || 'N/A'}`;
      const reason = `Feedback Reason: ${item['feedback_reason'] || 'N/A'}`;
      const detailsText = item['feedback_details'] || '';

      const wrappedUserInput = pdf.splitTextToSize(userInput, 190);
      const wrappedResponse = pdf.splitTextToSize(response, 190);
      const wrappedDetails = pdf.splitTextToSize(detailsText, 190);

      const estimatedLines =
        9 +
        wrappedUserInput.length +
        wrappedResponse.length +
        wrappedDetails.length;

      const blockHeight = estimatedLines * lineSpacing;
      if (!firstEntry && yPos + blockHeight > maxHeight) {
        pdf.addPage();
        yPos = 30;
      }

      firstEntry = false;

      pdf.text(`Entry ${index + 1}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Make: ${item.make || ''}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Model: ${item.model || ''}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Serial No. Range: ${item.equipment_serial_range || ''}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Equipment Code: ${item.equipment_code || ''}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`User ID: ${item.user_id || ''}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Submitted At: ${item.start_timestamp || ''}`, 10, yPos); yPos += lineSpacing;

      wrappedUserInput.forEach((line: string) => {
        if (yPos + lineSpacing > maxHeight) {
          pdf.addPage();
          yPos = 30;
        }
        pdf.text(line, 10, yPos);
        yPos += lineSpacing;
      });

      wrappedResponse.forEach((line: string) => {
        if (yPos + lineSpacing > maxHeight) {
          pdf.addPage();
          yPos = 30;
        }
        pdf.text(line, 10, yPos);
        yPos += lineSpacing;
      });

      if (yPos + lineSpacing > maxHeight) {
        pdf.addPage();
        yPos = 30;
      }
      pdf.text(feedback, 10, yPos); yPos += lineSpacing;

      if (yPos + lineSpacing > maxHeight) {
        pdf.addPage();
        yPos = 30;
      }
      pdf.text(reason, 10, yPos); yPos += lineSpacing;

      wrappedDetails.forEach((line: string) => {
        if (yPos + lineSpacing > maxHeight) {
          pdf.addPage();
          yPos = 30;
        }
        pdf.text(line, 10, yPos);
        yPos += lineSpacing;
      });

      yPos += lineSpacing;
    });

    pdf.save('dashboard.pdf');
  }

  openTransactionModal(element: any): void {
    this.dialog.open(ViewTransactionComponent, {
      data: element,
      width: '600px'
    });
  }

  clearAllFilters(): void {
    this.filters = {
      make: [],
      model: [],
      user_id: [],
      equipment_code: [],
      equipment_serial_range: [],
      response: '',
      user_input: '',
      feedBack: [],
      feedBackReason: [],
      feedBackDetails: '',
      applications: []
    };
    this.applyFilters();
  }
}